import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import { API } from "@/config";

// Use sessionStorage (tab-scoped, cleared on close) for auth tokens
function getStoredToken() {
  return sessionStorage.getItem("admin_token");
}

function setStoredToken(token) {
  if (token) sessionStorage.setItem("admin_token", token);
  else sessionStorage.removeItem("admin_token");
}

function authHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

async function fetchSafe(url, config) {
  try { return (await axios.get(url, config)).data; }
  catch (e) { console.error(`Failed to fetch ${url}:`, e.message); return null; }
}

export function useAdminAuth() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setChecking(false);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    }).then(({ data }) => {
      if (!cancelled) setUser(data);
    }).catch((err) => {
      console.error("Auth check failed:", err.message);
      setStoredToken(null);
      if (!cancelled) setUser(null);
    }).finally(() => {
      if (!cancelled) setChecking(false);
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount; API is a module constant

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders(), withCredentials: true });
    } catch (err) {
      console.error("Logout request failed:", err.message);
    }
    setStoredToken(null);
    setUser(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- authHeaders is a module function

  return { user, checking, login, logout };
}

export function useAdminData(user) {
  const [gallery, setGallery] = useState([]);
  const [events, setEvents] = useState([]);
  const [donations, setDonations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [collections, setCollections] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ hero_image: "", about_image: "" });

  const loadData = useCallback(async () => {
    if (!user) return;
    const config = { headers: authHeaders(), withCredentials: true };
    const [g, e, d, m, s, f, c] = await Promise.all([
      fetchSafe(`${API}/gallery`, config),
      fetchSafe(`${API}/events`, config),
      fetchSafe(`${API}/donations`, config),
      fetchSafe(`${API}/contact`, config),
      fetchSafe(`${API}/settings`, config),
      fetchSafe(`${API}/admin-files`, config),
      fetchSafe(`${API}/collections`, config),
    ]);
    if (g) setGallery(g);
    if (e) setEvents(e);
    if (d) setDonations(d);
    if (m) setMessages(m);
    if (s) setSiteSettings(s);
    if (f) setFiles(f);
    if (c) setCollections(c);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- API, authHeaders, fetchSafe are module-level stable refs

  useEffect(() => { loadData(); }, [loadData]);

  return { gallery, events, donations, messages, files, collections, siteSettings, loadData };
}

export { formatApiError, API, authHeaders };
